import React, { FC, useState } from 'react'
import {
  useDisclosure,
  Modal,
  ModalContent,
  ModalBody,
  Icon,
  Textarea,
  Button,
  Box,
  Image,
  Heading,
  Text,
  ModalCloseButton,
  Link,
} from '@chakra-ui/core'
import { isIOS, isSafari } from 'react-device-detect'
import { useRecoilValue, useRecoilState } from 'recoil'
import { configState, cartState, orderConfigState } from '../../recoil/atoms'
import { cartStatsState } from '../../recoil/selectors'
import { CartItem } from '../../components/CartItem'
import { LinkButton } from '../../lib/types'
import { Button as ButtonComponent } from '@engyalo/storefront-components'
import { useMutation, useQuery } from '@apollo/client'
import { useHistory, useParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { createOrderMutation, customCart } from '../../queries'
import { getStoreFront_orderConfig as IOrderConfig } from '@engyalo/storefront-types/lib/graphql/storefront'
import {
  createOrderVariables,
  createOrder_createOrder as ICreateOrder,
} from '@engyalo/storefront-types/lib/graphql/order'
import { IConfig, IProduct } from '@engyalo/storefront-types/lib/plugins/model'
import { customCartVariables, customCart as ICustomCart } from '@engyalo/storefront-types/lib/graphql/cart'
import { OrderStatus } from '@engyalo/storefront-types/lib/graphql/global-types'
import { ExecutionResult } from 'graphql'
import { MinAmountProgress } from '../../components/MinAmountProgress'

export const Cart: FC = () => {
  const { t } = useTranslation()
  const history = useHistory()
  const { customer, order } = useParams()
  const config = useRecoilValue<IConfig>(configState)
  const orderConfig = useRecoilValue<IOrderConfig>(orderConfigState)
  const [cart, setCart] = useRecoilState(cartState)
  const { totalNum, totalPrice } = useRecoilValue(cartStatsState)
  const [message, setMessage] = React.useState('')
  const [isCartVerified, setCartIsVerified] = React.useState(false)
  const [productToDelete, setProductToDelete] = useState<IProduct | null>(null)

  const { isOpen, onOpen, onClose } = useDisclosure()

  useQuery<ICustomCart, customCartVariables>(customCart, {
    variables: {
      storefrontID: config.storefrontId,
      orderConfigID: orderConfig.id || '',
      /**
       * This is ugly but needed by now. Product query gets an
       * autogenerated `__typename` property which is useful for caching
       * but `customCart` query doesn't expect it, thus we need to remove
       * it from the cart items list, however TS complains because it
       * doesn't find a `__typename` property in the IProduct interface :(
       * TODO: Remove @ts-ignore by ensuring the right types in all places
       */
      // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
      // @ts-ignore
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      cart: Object.values(cart).map(({ __typename, ...product }) => product),
    },
    skip: !config.enabledCustomCart || isCartVerified,
    onCompleted(data: ICustomCart) {
      const verifiedCart = data.customCart.reduce(
        (prev, cartItem) => ({
          ...prev,
          [cartItem?.id || '']: cartItem,
        }),
        {}
      )
      setCartIsVerified(true)
      setCart(verifiedCart)
    },
  })

  const deleteCart = (): void => {
    if (productToDelete) {
      deleteAction(productToDelete)
    } else {
      setCart({})
    }
    onClose()
  }
  const onTextChange = (e: React.FormEvent<HTMLInputElement>): void => {
    setMessage(e.currentTarget.value)
  }

  const [createOrder] = useMutation<ICreateOrder, createOrderVariables>(createOrderMutation, {
    variables: {
      order: {
        storefrontID: config.storefrontId,
        userID: orderConfig.userID || '',
        status: OrderStatus.ENABLE,
        provider: orderConfig.channelName,
        total: parseFloat(totalPrice),
        totalQty: totalNum,
        cart: Object.keys(cart).map((key) => cart[key]),
        note: message,
      },
      configId: order,
    },
    onCompleted: () => {
      if (orderConfig.channelName === 'whatsapp') {
        window.location.href = `http://wa.me/${orderConfig.channelID}`
      }
    },
  })

  const deleteItem = (product: IProduct): void => {
    setProductToDelete(product)
    onOpen()
  }

  const deleteAction = (product: IProduct): void => {
    const tempCart = { ...cart }
    if (product && product.cartId) {
      const currentSku = product.cartId
      if (tempCart[currentSku]) {
        delete tempCart[currentSku]
      }
    }
    setCart(tempCart)
    setProductToDelete(null)
  }

  const canPlaceOrder = !config.minAmount || Number(totalPrice) >= config.minAmount

  return (
    <Box h="100%" w="100%" d="flex" flexDir="column">
      {isOpen && (
        <Box position="absolute" opacity={0.5} backgroundColor="black" width="100%" height="100%" zIndex={50} />
      )}
      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalContent>
          <ModalCloseButton background="transparent" borderStyle="none" borderColor="unset" />
          <ModalBody>
            <Heading as="h3" fontSize="md" textAlign="center" paddingTop="40px" paddingBottom="16px">
              {productToDelete
                ? t('cart-removeQuestion', { product: productToDelete.name })
                : t('cart-removeAllQuestion')}
              <span role="img" aria-label="scream">
                😱
              </span>
            </Heading>
            <Box paddingBottom="20px">
              <ButtonComponent onClick={deleteCart} danger>
                {productToDelete ? t('cart-remove') : t('cart-removeAll')}
              </ButtonComponent>
            </Box>
          </ModalBody>
        </ModalContent>
      </Modal>
      <Box h="40px" d="flex">
        <LinkButton
          to={`/${customer}/${order}`}
          h="100%"
          w="32px"
          background="transparent"
          borderColor="transparent"
          size="lg"
          aria-label="cart"
          icon="chevron-left"
        />
        <Image alt="logo" h="36px" alignSelf="center" margin="auto" src={config.logo} />
      </Box>
      <Box padding="12px 12px 8px 16px" borderBottom="1px solid" borderColor="gray.200">
        <Heading as="h2" fontSize="lg" fontWeight="normal">
          {t('cart-title')}
        </Heading>
        {totalNum > 0 && (
          <Box display="flex" justifyContent="space-between">
            <Text fontSize="sm" color="gray.100">
              {t('cart-resume', { count: totalNum })}
            </Text>
            <Button
              color="primary.500"
              variant="link"
              border="transparent"
              fontSize="sm"
              textDecor="underline"
              backgroundColor="transparent"
              fontWeight="normal"
              onClick={onOpen}
            >
              {t('cart-cleanCart')}
            </Button>
          </Box>
        )}
      </Box>
      {/* Body */}
      <Box display="flex" flexDir="column" flex="1" overflowY={isSafari || isIOS ? 'unset' : 'auto'}>
        {totalNum > 0 ? (
          <Box display="grid" overflowY="auto" height="100%" gridAutoRows="min-content">
            {Object.keys(cart).map((key) => (
              <CartItem deleteItem={deleteItem} key={key} product={cart[key]} />
            ))}
          </Box>
        ) : (
          <Box display="flex" flexDir="column" justifyContent="center" alignItems="center" h="100%">
            <Icon name="settings" size="48px" paddingBottom="8px" />
            <Text fontSize="md" textAlign="center" whiteSpace="pre-line">
              {t('cart-emptyMessage')}
            </Text>
            <Link
              color="primary.500"
              href={`/${customer}/${order}`}
              aria-label="backToCart"
              border="transparent"
              fontSize="sm"
              textDecor="underline"
              backgroundColor="transparent"
              fontWeight="normal"
              paddingTop="32px"
            >
              {t('cart-backCatalogue')}
            </Link>
          </Box>
        )}
      </Box>
      {/* Body */}
      {totalNum > 0 && config.enabledOrderNote && (
        <Box paddingX="8px">
          <Text display="inline-flex" fontSize="sm">
            {t('cart-observations')}
          </Text>
          <Text display="inline-flex" paddingLeft="4px" fontSize="sm" color="gray.100">
            {t('cart-observationsAlter')}
          </Text>
          <Textarea
            marginTop="8px"
            value={message}
            onChange={onTextChange}
            placeholder={t('cart-observationsPlaceholder')}
            backgroundColor="gray.300"
            borderColor="gray.200"
            borderRadius="4px"
            resize="none"
            size="sm"
          />
        </Box>
      )}
      {/* Footer */}
      <Box zIndex={1} paddingY="12px" paddingX="8px" boxShadow="md" backgroundColor="white">
        {!canPlaceOrder ? (
          <MinAmountProgress
            currencyPrefix={config.currencyPrefix}
            currentAmount={Number(totalPrice)}
            minAmount={config.minAmount || 0}
          />
        ) : null}
        <Box
          display="flex"
          width="100%"
          paddingY="12px"
          paddingX="16px"
          backgroundColor="primary.500"
          borderColor="transparent"
          fontWeight="500"
          color="white"
          fontFamily="body"
          fontSize="11px"
          borderRadius="4px"
          justifyContent="space-between"
          cursor="pointer"
          onClick={(): boolean | Promise<ExecutionResult<ICreateOrder>> => {
            if (!canPlaceOrder) {
              history.push(`/${customer}/${order}`)
              return false
            }
            return createOrder()
          }}
        >
          {canPlaceOrder ? (
            <>
              <Text fontSize="lg" alignSelf="center">
                {t('cart-pay')}
              </Text>
              <Text fontSize="lg" alignSelf="center">{`${config.currencyPrefix} ${totalPrice}`}</Text>
            </>
          ) : (
            <Text fontSize="lg" textAlign="center" flex="1">
              {t('cart-goToCatalogue')}
            </Text>
          )}
        </Box>
      </Box>
      {/* Footer */}
    </Box>
  )
}
